import {
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Rebus Team Service
 * Handles all team-related operations for rebus sessions
 */

/**
 * Adds a team to a rebus session
 * @param {string} sessionId - Session ID (room code / PIN)
 * @param {string} teamName - Team display name
 * @returns {Promise<Object>} Team data with generated ID
 */
export const joinRebusSession = async (sessionId, teamName, memberName = '') => {
  try {
    // Validate PIN format (6 digits)
    if (!sessionId || typeof sessionId !== 'string' || !/^[0-9]{6}$/.test(sessionId.trim())) {
      throw new Error('Ugyldig PIN-kode');
    }

    // Validate team name (2-30 characters)
    if (!teamName || typeof teamName !== 'string') {
      throw new Error('Lagnamn er påkravd');
    }
    const trimmedName = teamName.trim();
    if (trimmedName.length < 2 || trimmedName.length > 30) {
      throw new Error('Lagnamn må vere mellom 2 og 30 teikn');
    }

    // Validate member name if provided
    const trimmedMember = memberName?.trim() || '';

    // Fetch session to verify it exists
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta. Sjekk PIN-koden.');
    }

    const sessionData = sessionSnap.data();

    // Check if session is in valid state to join
    if (sessionData.status === 'finished') {
      throw new Error('Denne rebus-økta er allereie avslutta');
    }

    // Check if a team with this name already exists in the session
    const existingTeams = sessionData.teams || {};
    const existingTeam = Object.values(existingTeams).find(
      t => t.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingTeam) {
      // Join existing team — add member name
      if (trimmedMember) {
        const currentMembers = existingTeam.members || [];
        if (!currentMembers.includes(trimmedMember)) {
          await updateDoc(sessionRef, {
            [`teams.${existingTeam.id}.members`]: [...currentMembers, trimmedMember]
          });
        }
      }
      return existingTeam;
    }

    // Create new team
    const teamId = `t${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    const team = {
      id: teamId,
      name: trimmedName,
      joinedAt: new Date().toISOString(),
      members: trimmedMember ? [trimmedMember] : [],
      stationResults: {},
      extraTaskResults: {},
      totalScore: 0
    };

    // Add team to session
    await updateDoc(sessionRef, {
      [`teams.${teamId}`]: team
    });

    return team;
  } catch (error) {
    console.error('Error joining rebus session:', error);
    throw new Error(`Klarte ikkje bli med i rebus-økt: ${error.message}`);
  }
};

/**
 * Submits an answer for a station question
 * Auto-scores if correctAnswer exists on the question (case-insensitive trim match)
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} stationId - Station ID
 * @param {string} questionId - Question ID
 * @param {string} answer - The team's answer
 * @param {Object} rebusData - The full rebus data (for checking correct answers)
 * @returns {Promise<Object>} Result with points and correctness
 */
export const submitStationAnswer = async (sessionId, teamId, stationId, questionId, answer, rebusData) => {
  try {
    if (!sessionId || !teamId || !stationId || !questionId) {
      throw new Error('Manglar påkravde parametrar');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    const sessionData = sessionSnap.data();

    // Verify session is active
    if (sessionData.status !== 'active') {
      throw new Error('Mikkelchase er ikkje starta enno');
    }

    // Verify team exists
    if (!sessionData.teams || !sessionData.teams[teamId]) {
      throw new Error('Fann ikkje laget i økta');
    }

    // Check if already answered
    const existingResults = sessionData.teams[teamId].stationResults || {};
    const stationResult = existingResults[stationId] || {};
    const existingAnswers = stationResult.answers || {};

    if (existingAnswers[questionId]) {
      // Allow resubmit if previous answer was rejected
      if (existingAnswers[questionId].approved === false) {
        // Clear the rejected answer to allow resubmit
      } else {
        throw new Error('Svar er allereie sendt for dette spørsmålet');
      }
    }

    // Find the question in rebusData to check correctAnswer
    const station = rebusData.stations.find(s => s.id === stationId);
    if (!station) {
      throw new Error('Fann ikkje stasjonen i rebusen');
    }

    const question = station.questions.find(q => q.id === questionId);
    if (!question) {
      throw new Error('Fann ikkje spørsmålet i rebusen');
    }

    // Scoring logic
    let isCorrect = null;
    let pointsEarned = 0;
    const manualScoring = rebusData.settings?.manualScoring === true;

    if (manualScoring) {
      // Manual scoring: all answers go to admin for review
      isCorrect = null;
      pointsEarned = 0;
    } else if (question.type === 'multiple_choice') {
      // For multiple choice, find the selected answer and check isCorrect
      const selectedAnswer = question.answers.find(a => a.id === answer);
      if (!selectedAnswer) {
        throw new Error('Ugyldig svar-ID');
      }
      isCorrect = selectedAnswer.isCorrect;
      pointsEarned = isCorrect ? (question.points || 1) : 0;
    } else if (question.correctAnswer) {
      // For text questions with correctAnswer, case-insensitive trim match
      isCorrect = answer.trim().toLowerCase() === question.correctAnswer.trim().toLowerCase();
      pointsEarned = isCorrect ? (question.points || 1) : 0;
    } else {
      // No correct answer defined, cannot auto-score
      isCorrect = null;
      pointsEarned = 0;
    }

    const answerResult = {
      answer: answer,
      isCorrect: isCorrect,
      points: pointsEarned,
      submittedAt: new Date().toISOString()
    };

    await updateDoc(sessionRef, {
      [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}`]: answerResult
    });

    // Recalculate total score if points were earned
    if (pointsEarned > 0) {
      await recalculateTeamScore(sessionId, teamId);
    }

    return {
      isCorrect,
      pointsEarned,
      questionId,
      stationId
    };
  } catch (error) {
    console.error('Error submitting station answer:', error);
    throw new Error(`Klarte ikkje sende svar: ${error.message}`);
  }
};

/**
 * Marks a station as completed for a team
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} stationId - Station ID
 * @returns {Promise<void>}
 */
export const completeStation = async (sessionId, teamId, stationId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    const sessionData = sessionSnap.data();

    // Verify session is active
    if (sessionData.status !== 'active') {
      throw new Error('Mikkelchase er ikkje starta enno');
    }

    if (!sessionData.teams || !sessionData.teams[teamId]) {
      throw new Error('Fann ikkje laget i økta');
    }

    await updateDoc(sessionRef, {
      [`teams.${teamId}.stationResults.${stationId}.completedAt`]: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error completing station:', error);
    throw new Error(`Klarte ikkje fullføre stasjon: ${error.message}`);
  }
};

/**
 * Submits an extra task answer (text or photo)
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} taskId - Extra task ID
 * @param {Object} submission - { answer?: string, photoUrl?: string }
 * @returns {Promise<void>}
 */
export const submitExtraTask = async (sessionId, teamId, taskId, submission) => {
  try {
    if (!sessionId || !teamId || !taskId) {
      throw new Error('Manglar påkravde parametrar');
    }

    if (!submission || (!submission.answer && !submission.photoUrl)) {
      throw new Error('Må sende inn eit svar eller eit bilete');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    const sessionData = sessionSnap.data();

    if (sessionData.status !== 'active') {
      throw new Error('Mikkelchase er ikkje starta enno');
    }

    if (!sessionData.teams || !sessionData.teams[teamId]) {
      throw new Error('Fann ikkje laget i økta');
    }

    // Check if already submitted
    const existingExtra = sessionData.teams[teamId].extraTaskResults || {};
    if (existingExtra[taskId]) {
      // Allow resubmit if previous submission was rejected
      if (existingExtra[taskId].approved === false) {
        // Clear the rejected submission to allow resubmit
      } else {
        throw new Error('Ekstraoppgåve er allereie levert');
      }
    }

    const taskResult = {
      answer: submission.answer || null,
      photoUrl: submission.photoUrl || null,
      comment: submission.comment || null,
      submittedAt: new Date().toISOString(),
      points: 0,
      approved: null // null = pending review
    };

    await updateDoc(sessionRef, {
      [`teams.${teamId}.extraTaskResults.${taskId}`]: taskResult
    });

  } catch (error) {
    console.error('Error submitting extra task:', error);
    throw new Error(`Klarte ikkje levere ekstraoppgåve: ${error.message}`);
  }
};

/**
 * Recalculates a team's total score from all sources:
 * - Station answer points
 * - Challenge scores
 * - Approved extra task points
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @returns {Promise<number>} New total score
 */
export const recalculateTeamScore = async (sessionId, teamId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    const sessionData = sessionSnap.data();

    if (!sessionData.teams || !sessionData.teams[teamId]) {
      throw new Error('Fann ikkje laget i økta');
    }

    const team = sessionData.teams[teamId];
    let totalScore = 0;

    // Sum station answer points
    const stationResults = team.stationResults || {};
    for (const stationId of Object.keys(stationResults)) {
      const stationResult = stationResults[stationId];

      // Sum answer points
      const answers = stationResult.answers || {};
      for (const questionId of Object.keys(answers)) {
        const answerData = answers[questionId];
        totalScore += answerData.points || 0;
      }

      // Add challenge score
      if (typeof stationResult.challengeScore === 'number') {
        totalScore += stationResult.challengeScore;
      }
    }

    // Sum approved extra task points
    const extraTaskResults = team.extraTaskResults || {};
    for (const taskId of Object.keys(extraTaskResults)) {
      const taskResult = extraTaskResults[taskId];
      if (taskResult.approved === true) {
        totalScore += taskResult.points || 0;
      }
    }

    // Add bonus points
    if (typeof team.bonusPoints === 'number') {
      totalScore += team.bonusPoints;
    }

    // Update totalScore in Firestore
    await updateDoc(sessionRef, {
      [`teams.${teamId}.totalScore`]: totalScore
    });

    return totalScore;
  } catch (error) {
    console.error('Error recalculating team score:', error);
    throw new Error(`Klarte ikkje rekne ut poengsum: ${error.message}`);
  }
};
